import type {
  Gender,
  MeasurementUnit,
  SizeGuideData,
} from '@app/components/SizeGuide/types';
import { Divider, List } from '@redbubble/design-system';
import { UserInfo } from '@redbubble/next/types';
import { useNavigation, useUserInfo } from '@redbubble/next-provider';
import { useEffect } from 'react';
import {
  InventoryItemGaCode,
  ProductTypeId,
  WorkId,
} from 'types/shop-bff/types';
import { getTimezoneOffset } from '../../utils/dates';
import { DeliveryTrustSignal } from './DeliveryTrustSignal';
import { ReturnPolicyTrustSignal } from './ReturnPolicyTrustSignal';
import { SizeGuideTrustSignal } from './SizeGuideTrustSignal';
import { useDeliveryDates } from './utils';

export interface ProductTrustSignalsProps {
  productTypeId: ProductTypeId;
  showSizeGuideButton: boolean;
  workId: WorkId;
  analyticsCode: InventoryItemGaCode;
  sizeGuideData?: SizeGuideData;
  initialGender?: Gender;
  initialMeasurementUnit?: MeasurementUnit;
}

export const ProductTrustSignals = ({
  productTypeId,
  showSizeGuideButton,
  workId,
  analyticsCode,
  sizeGuideData,
  initialGender,
  initialMeasurementUnit,
}: ProductTrustSignalsProps) => {
  const [{ locale, country: countryCode }] = useUserInfo<UserInfo>();
  const { loading } = useNavigation();

  const {
    deliveryDates,
    loading: loadingDeliveryDates,
    refresh: refreshDeliveryDates,
  } = useDeliveryDates();

  useEffect(() => {
    refreshDeliveryDates({
      countryCode,
      locale,
      productTypeId,
      timezoneOffset: getTimezoneOffset(),
    });
  }, [countryCode, locale, productTypeId, refreshDeliveryDates]);

  return (
    <>
      <Divider />
      <List relaxed divided>
        <DeliveryTrustSignal
          deliveryDates={deliveryDates}
          countryCode={countryCode}
          loading={loading || loadingDeliveryDates}
          locale={locale}
        />
        <ReturnPolicyTrustSignal locale={locale} />
        {showSizeGuideButton && sizeGuideData != null && (
          <SizeGuideTrustSignal
            productTypeId={productTypeId}
            workId={workId}
            sizeGuideData={sizeGuideData}
            initialGender={initialGender}
            initialMeasurementUnit={initialMeasurementUnit}
          />
        )}
      </List>
      <Divider />
    </>
  );
};
